import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import {
  Plus,
  Search,
  Eye,
  Download,
  Share2,
  AlertCircle,
  Clock,
  ArrowRightLeft,
  Calendar,
  Filter,
  Loader2,
  FileX2,
  Settings,
  ChevronRight,
  Mail,
  MessageCircle,
} from "lucide-react";
import { formatPrice } from "../../utils/productUtils";
import AdminInvoiceService from "../../services/admin/AdminInvoiceService";
import { useNotification } from "../../context/NotificationContext";
import { useAdminPermissions } from "../../hooks/useAdminPermissions";
import { InvoicesSkeleton } from "../../components/admin/InvoicesPage/InvoicesSkeleton";
import { useComingSoon } from "../../utils/useComingSoon";

const INVOICE_STATUSES = [
  { id: "all", label: "All Invoices", color: "bg-gray-100 text-gray-700" },
  { id: "generated", label: "Generated", color: "bg-gray-100 text-gray-600 border-gray-200" },
  { id: "approved", label: "Approved", color: "bg-teal-50 text-teal-600 border-teal-100" },
  { id: "in_use", label: "In Use", color: "bg-purple-50 text-purple-600 border-purple-100" },
  { id: "completed", label: "Completed", color: "bg-green-50 text-green-600 border-green-100" },
  { id: "cancelled", label: "Cancelled", color: "bg-red-50 text-red-600 border-red-100" },
];

const getStatusBadge = (status) => {
  if (!status) return null;
  const normalizedStatus = String(status).toLowerCase();
  const s = INVOICE_STATUSES.find((item) => item.id === normalizedStatus);

  if (!s) {
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-medium border bg-gray-50 text-gray-500 border-gray-100">
        {status}
      </span>
    );
  }

  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${s.color}`}>
      {s.label}
    </span>
  );
};

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { showComingSoon } = useComingSoon();
  const { checkPermission, hasPermission } = useAdminPermissions();

  const [invoices, setInvoices] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [activeStatus, setActiveStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [settingsExist, setSettingsExist] = useState(null);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const [sharingIds, setSharingIds] = useState(new Set());
  const [shareDropdownId, setShareDropdownId] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const shareDropdownRef = useRef(null);

  const statusMap = {
    generated: {
      label: "Generated",
      color: "text-gray-600 bg-gray-100 border-gray-200",
      next: ["approved", "cancelled"],
    },
    approved: {
      label: "Approved",
      color: "text-teal-600 bg-teal-50 border-teal-100",
      next: ["in_use", "cancelled"],
    },
    in_use: {
      label: "In Use",
      color: "text-purple-600 bg-purple-50 border-purple-100",
      next: ["completed"],
    },
    completed: {
      label: "Completed",
      color: "text-green-600 bg-green-50 border-green-100",
      next: [],
    },
    cancelled: {
      label: "Cancelled",
      color: "text-red-600 bg-red-50 border-red-100",
      next: [],
    },
  };

  // Close share dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (shareDropdownRef.current && !shareDropdownRef.current.contains(e.target)) {
        setShareDropdownId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when status filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeStatus]);

  // Single fetch effect — only source of truth
  useEffect(() => {
    let cancelled = false;

    const fetchInvoices = async () => {
      setIsLoading(true);
      try {
        const params = { page: currentPage, limit: 20 };
        if (activeStatus !== "all") params.status = activeStatus;
        if (debouncedSearch) params.search = debouncedSearch;

        const result = await AdminInvoiceService.list(params);

        if (cancelled) return;

        setInvoices(result.data || []);
        setPagination(result.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
        if (result.status_counts) setStatusCounts(result.status_counts);
        setSettingsExist(result.invoice_setting_exists);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to fetch invoices:", error);
        showNotification({
          type: "failed",
          title: "Load Error",
          message: "Failed to load invoices. Please try again."
        });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchInvoices();

    return () => { cancelled = true; };
  }, [currentPage, activeStatus, debouncedSearch, showNotification]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setCurrentPage(newPage);
    }
  };

  const handleGenerateInvoice = () => {
    if (!checkPermission("admin", "generate invoices")) return;
    if (isLoading) return;
    if (settingsExist === false) {
      setIsSettingsModalOpen(true);
    } else {
      navigate("/admin/invoices/create");
    }
  };

  const handleDownload = async (invoiceId, invoiceNumber) => {
    if (downloadingIds.has(invoiceId)) return;

    setDownloadingIds(prev => new Set(prev).add(invoiceId));

    try {
      const response = await AdminInvoiceService.download(invoiceId);
      const blob = response.data;

      if (!blob || blob.size === 0) {
        showNotification({
          type: "info",
          title: "File Unavailable",
          message: "The invoice PDF could not be found. It may still be generating."
        });
        return;
      }

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `St-Stephens-Rentals-${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotification({
        type: "success",
        title: "Download Started",
        message: `Invoice ${invoiceNumber} is being downloaded.`
      });
    } catch (error) {
      console.error("Download error:", error);
      showNotification({
        type: "failed",
        title: "Download Failed",
        message: "Failed to download the invoice. Please try again."
      });
    } finally {
      setDownloadingIds(prev => {
        const next = new Set(prev);
        next.delete(invoiceId);
        return next;
      });
    }
  };

  const handleShareToggle = (invoiceId) => {
    setShareDropdownId(prev => prev === invoiceId ? null : invoiceId);
  };

  const handleShareEmail = async (invoiceId, invoiceNumber) => {
    if (sharingIds.has(invoiceId)) return;
    setShareDropdownId(null);
    setSharingIds(prev => new Set(prev).add(invoiceId));

    showNotification({
      type: "info",
      title: "Sharing Invoice",
      message: `Sending ${invoiceNumber} via email to the customer...`
    });

    try {
      const result = await AdminInvoiceService.share(invoiceId);
      if (result.status === "success") {
        showNotification({
          type: "success",
          title: "Invoice Sent",
          message: result.message || `Invoice ${invoiceNumber} sent successfully.`
        });
      }
    } catch (error) {
      console.error("Share error:", error);
      showNotification({
        type: "failed",
        title: "Share Failed",
        message: error.response?.data?.message || "Failed to share the invoice. Please try again."
      });
    } finally {
      setSharingIds(prev => {
        const next = new Set(prev);
        next.delete(invoiceId);
        return next;
      });
    }
  };

  const handleShareWhatsApp = (invoiceNumber) => {
    setShareDropdownId(null);
    showComingSoon({
      title: "WhatsApp Sharing Coming Soon!",
      message: `We're building WhatsApp sharing for ${invoiceNumber}. Stay tuned!`
    });
  };

  const handleStatusChangeClick = (invoice) => {
    setSelectedInvoice(invoice);
    setShowStatusModal(true);
  };

  const handleStatusChange = async (newStatus) => {
    if (!checkPermission("admin", "update invoice status")) return;
    if (updatingStatus || !selectedInvoice) return;
    setUpdatingStatus(newStatus);

    try {
      const result = await AdminInvoiceService.updateStatus(selectedInvoice.id, newStatus);
      if (result.status === "success") {
        // Update local list state
        setInvoices(prev => prev.map(inv =>
          inv.id === selectedInvoice.id ? result.data : inv
        ));

        // Update status counts if they exist in state
        setStatusCounts(prev => {
          const next = { ...prev };
          const oldStatus = selectedInvoice.status;
          if (next[oldStatus]) next[oldStatus]--;
          if (next[newStatus] !== undefined) next[newStatus]++;
          return next;
        });

        setShowStatusModal(false);
        showNotification({
          type: "success",
          title: "Status Updated",
          message: `Invoice status changed to ${statusMap[newStatus].label}`
        });
      }
    } catch (error) {
      console.error("Failed to update status:", error);
      showNotification({
        type: "failed",
        title: "Update Error",
        message: error.response?.data?.message || "Failed to update status."
      });
    } finally {
      setUpdatingStatus(null);
    }
  };

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8 max-w-[1600px] mx-auto space-y-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#111111] dark:text-white font-poppins tracking-tight">
              Invoices
            </h1>
            <p className="text-[#666666] dark:text-[#A0A0A0] text-sm font-inter">
              Manage rental invoices and track lifecycle
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/invoices/settings"
              onClick={(e) => {
                if (!checkPermission("super_admin", "access invoice settings")) {
                  e.preventDefault();
                }
              }}
              className="flex items-center gap-2 px-5 py-2.5 border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#4B5563] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] rounded-xl font-semibold transition-all active:scale-95 shadow-sm"
              title="Invoice Settings"
            >
              <Settings size={18} />
              <span className="hidden md:inline">Settings</span>
            </Link>
            <button
              onClick={handleGenerateInvoice}
              disabled={isLoading}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold shadow-lg transition-all active:scale-95 ${isLoading
                ? "bg-gray-300 dark:bg-[#333333] text-gray-500 cursor-not-allowed shadow-none"
                : "bg-[#62397f] hover:bg-[#7C3AED] text-white shadow-purple-200 dark:shadow-none"
                }`}
            >
              {isLoading ? (
                <Loader2 size={18} className="animate-spin text-gray-400" />
              ) : (
                <Plus size={18} />
              )}
              Generate Invoice
            </button>
          </div>
        </div>

        {/* Status Pipeline Bar */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {INVOICE_STATUSES.map((status) => (
            <button
              key={status.id}
              onClick={() => setActiveStatus(status.id)}
              className={`p-4 rounded-2xl border transition-all text-left group ${activeStatus === status.id
                ? "bg-white dark:bg-[#1A1A1A] border-[#62397f] ring-1 ring-[#62397f] shadow-sm"
                : "bg-white dark:bg-[#1A1A1A] border-[#E5E7EB] dark:border-[#2A2A2A] hover:border-[#62397f]"
                }`}
            >
              <p
                className={`text-xs font-medium mb-1 transition-colors ${activeStatus === status.id
                  ? "text-[#62397f]"
                  : "text-[#666666] dark:text-[#A0A0A0]"
                  }`}
              >
                {status.label}
              </p>
              {isLoading ? (
                <div className="h-7 w-12 bg-gray-200 dark:bg-[#262626] rounded-lg animate-pulse mt-1"></div>
              ) : (
                <p className="text-xl font-bold text-[#111111] dark:text-white">
                  {statusCounts[status.id] ?? 0}
                </p>
              )}
            </button>
          ))}
        </div>

        {/* Filter Bar */}
        <div className="bg-white dark:bg-[#1A1A1A] p-4 rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-sm flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
              size={18}
            />
            <input
              type="text"
              placeholder="Search by ID, customer name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#F9FAFB] dark:bg-[#262626] border border-[#E5E7EB] dark:border-[#333333] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#62397f] transition-all"
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button className="flex items-center gap-2 px-4 py-2.5 border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-sm font-medium text-[#4B5563] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors">
              <Calendar size={16} />
              Date Range
            </button>
            <button className="flex items-center gap-2 px-4 py-2.5 border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl text-sm font-medium text-[#4B5563] dark:text-[#A0A0A0] hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors">
              <Filter size={16} />
              All Filters
            </button>
            <div className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-xl">
              <input
                type="checkbox"
                id="overdue"
                className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
              />
              <label
                htmlFor="overdue"
                className="text-sm font-medium text-red-700 dark:text-red-400"
              >
                Overdue Only
              </label>
            </div>
          </div>
        </div>

        {/* Invoice Table */}
        {isLoading ? (
          <InvoicesSkeleton />
        ) : (
          <div className="bg-white dark:bg-[#1A1A1A] rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] overflow-visible pb-40 shadow-sm">
            <div className="overflow-x-auto overflow-y-visible">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#F9FAFB] dark:bg-[#262626] border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
                    <th className="px-6 py-4 text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wider">
                      Invoice ID
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wider text-center">
                      Items
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wider">
                      Return Date
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold text-[#6B7280] dark:text-[#A0A0A0] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E7EB] dark:divide-[#2A2A2A]">
                  {invoices.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-16 text-center">
                        <FileX2 size={36} className="text-[#D1D5DB] dark:text-[#4B5563] mx-auto mb-3" />
                        <p className="text-sm font-medium text-[#6B7280] dark:text-[#A0A0A0]">No invoices found</p>
                        <p className="text-xs text-[#9CA3AF] mt-1">Try adjusting your filters or create a new invoice.</p>
                      </td>
                    </tr>
                  ) : (
                    invoices.map((invoice) => (
                      <tr
                        key={invoice.id}
                        className="hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors group"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span
                              onClick={() => navigate(`/admin/invoices/${invoice.id}`)}
                              className="font-semibold text-sm text-[#111111] dark:text-white cursor-pointer select-none hover:text-[#62397f] transition-colors"
                            >
                              {invoice.invoice_number}
                            </span>
                            {invoice.overdue && (
                              <div className="group/tooltip relative">
                                <AlertCircle size={14} className="text-red-500" />
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-red-600 text-white text-[10px] rounded opacity-0 group-hover/tooltip:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                                  Overdue
                                </div>
                              </div>
                            )}
                          </div>
                          <p className="text-[11px] text-[#9CA3AF]">
                            {invoice.created_at}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p
                            onClick={() => navigate(`/admin/customers/${invoice.customer_id}`)}
                            className="text-sm font-medium text-[#111111] dark:text-white cursor-pointer hover:text-[#62397f] transition-colors"
                          >
                            {typeof invoice.customer === 'object' ? invoice.customer?.name : invoice.customer}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="px-2 py-0.5 bg-gray-100 dark:bg-[#333333] rounded text-xs text-gray-600 dark:text-gray-300 font-medium">
                            {invoice.items_count ?? (Array.isArray(invoice.items) ? invoice.items.length : 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-bold text-[#111111] dark:text-white">
                          ₦{formatPrice(invoice.total)}
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-sm text-[#4B5563] dark:text-[#A0A0A0]">
                            <Clock size={14} />
                            {invoice.rental_end}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/admin/invoices/${invoice.id}`)}
                              className="p-1.5 text-[#6B7280] dark:text-[#A0A0A0] hover:bg-[#F3F4F6] dark:hover:bg-[#333333] rounded-lg transition-colors"
                              title="View"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => handleDownload(invoice.id, invoice.invoice_number)}
                              disabled={downloadingIds.has(invoice.id)}
                              className={`p-1.5 text-[#6B7280] dark:text-[#A0A0A0] hover:bg-[#F3F4F6] dark:hover:bg-[#333333] rounded-lg transition-colors disabled:opacity-50`}
                              title="Download PDF"
                            >
                              {downloadingIds.has(invoice.id) ? (
                                <Loader2 size={18} className="animate-spin text-[#62397f]" />
                              ) : (
                                <Download size={18} />
                              )}
                            </button>
                            <div className="relative" ref={shareDropdownId === invoice.id ? shareDropdownRef : null}>
                              <button
                                onClick={() => handleShareToggle(invoice.id)}
                                disabled={sharingIds.has(invoice.id)}
                                className={`p-1.5 text-[#6B7280] dark:text-[#A0A0A0] hover:bg-[#F3F4F6] dark:hover:bg-[#333333] rounded-lg transition-colors disabled:opacity-50`}
                                title="Share"
                              >
                                {sharingIds.has(invoice.id) ? (
                                  <Loader2 size={18} className="animate-spin text-[#62397f]" />
                                ) : (
                                  <Share2 size={18} />
                                )}
                              </button>
                              {shareDropdownId === invoice.id && (
                                <div className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-[#1A1A1A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-xl shadow-xl z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                                  <button
                                    onClick={() => handleShareEmail(invoice.id, invoice.invoice_number)}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-[#111111] dark:text-white hover:bg-[#F3F4F6] dark:hover:bg-[#262626] transition-colors"
                                  >
                                    <Mail size={16} className="text-[#62397f]" />
                                    Send via Email
                                  </button>
                                  <button
                                    onClick={() => handleShareWhatsApp(invoice.invoice_number)}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-[#111111] dark:text-white hover:bg-[#F3F4F6] dark:hover:bg-[#262626] transition-colors border-t border-[#E5E7EB] dark:border-[#2A2A2A]"
                                  >
                                    <MessageCircle size={16} className="text-green-500" />
                                    Send via WhatsApp
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="h-4 w-px bg-gray-200 dark:bg-gray-700 mx-1"></div>
                            <button
                              onClick={() => handleStatusChangeClick(invoice)}
                              className="p-1.5 text-[#6B7280] dark:text-[#A0A0A0] hover:bg-[#F3F4F6] dark:hover:bg-[#333333] rounded-lg transition-colors"
                              title="Update Status"
                            >
                              <ArrowRightLeft size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!isLoading && pagination.pages > 0 && (
              <div className="px-6 py-4 bg-[#F9FAFB] dark:bg-[#262626] border-t border-[#E5E7EB] dark:border-[#2A2A2A] flex items-center justify-between">
                <p className="text-sm text-[#6B7280] dark:text-[#A0A0A0]">
                  Showing {((pagination.page - 1) * pagination.limit) + 1} to{" "}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
                  {pagination.total} results
                </p>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-lg text-sm text-[#6B7280] dark:text-[#A0A0A0] disabled:opacity-50"
                    disabled={pagination.page <= 1}
                    onClick={() => handlePageChange(pagination.page - 1)}
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium ${pagination.page === pageNum
                          ? "bg-[#62397f] text-white shadow-sm"
                          : "border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#6B7280] dark:text-[#A0A0A0]"
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    className="px-3 py-1.5 border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-lg text-sm text-[#6B7280] dark:text-[#A0A0A0] disabled:opacity-50"
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => handlePageChange(pagination.page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <SettingsRequiredModal
          isOpen={isSettingsModalOpen}
          onClose={() => setIsSettingsModalOpen(false)}
          onNavigate={() => {
            setIsSettingsModalOpen(false);
            navigate("/admin/invoices/settings");
          }}
        />

        {/* Status Update Modal */}
        {showStatusModal && selectedInvoice && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-[#1A1A1A] w-full max-w-md rounded-3xl shadow-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] overflow-hidden">
              <div className="p-6 border-b border-[#E5E7EB] dark:border-[#2A2A2A]">
                <h3 className="text-lg font-bold text-[#111111] dark:text-white font-poppins">
                  Update Invoice Status
                </h3>
                <p className="text-xs text-[#6B7280] dark:text-[#A0A0A0]">
                  Invoice: <span className="font-bold">{selectedInvoice.invoice_number}</span> | Current:{" "}
                  <span className="font-bold">{statusMap[selectedInvoice.status]?.label || selectedInvoice.status}</span>
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#9CA3AF] uppercase">
                    Next Valid Status
                  </label>
                  <div className="grid grid-cols-1 gap-2">
                    {statusMap[selectedInvoice.status] && statusMap[selectedInvoice.status].next.length > 0 ? (
                      statusMap[selectedInvoice.status].next.map((nextStatus) => (
                        <button
                          key={nextStatus}
                          onClick={() => handleStatusChange(nextStatus)}
                          disabled={!!updatingStatus}
                          className={`flex items-center justify-between p-4 rounded-2xl border border-gray-100 dark:border-[#2A2A2A] hover:border-[#62397f] hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          <span className="text-sm font-bold text-[#111111] dark:text-white">
                            {statusMap[nextStatus].label}
                          </span>
                          {updatingStatus === nextStatus ? (
                            <Loader2 size={18} className="animate-spin text-[#62397f]" />
                          ) : (
                            <ChevronRight
                              size={18}
                              className="text-gray-300 group-hover:text-[#62397f]"
                            />
                          )}
                        </button>
                      ))
                    ) : (
                      <div className="p-4 bg-gray-50 dark:bg-[#262626] rounded-2xl border border-dashed border-gray-200 text-center">
                        <p className="text-sm text-gray-500 italic">
                          No further status updates available
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowStatusModal(false)}
                  disabled={!!updatingStatus}
                  className="w-full py-3 text-sm font-bold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

// Internal Settings Error Modal
const SettingsRequiredModal = ({ isOpen, onClose, onNavigate }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white dark:bg-[#1A1A1A] w-full max-w-md rounded-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] shadow-2xl p-6 lg:p-8 transform transition-all animate-in zoom-in-95 duration-200">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-amber-50 dark:bg-amber-900/10 rounded-full flex items-center justify-center text-amber-500 mb-6 border border-amber-100 dark:border-amber-900/20">
            <AlertCircle size={32} />
          </div>

          <h3 className="text-xl font-bold text-[#111111] dark:text-white mb-2 font-poppins">
            Configuration Required
          </h3>
          <p className="text-[#6B7280] dark:text-[#A0A0A0] text-sm leading-relaxed mb-8">
            Before you can generate an invoice, you must first complete the <strong>Invoice Settings</strong> (Bank Details, Fees, etc.). This ensures all calculations and payment instructions are correct.
          </p>

          <div className="grid grid-cols-2 gap-3 w-full">
            <button
              onClick={onClose}
              className="px-4 py-2.5 border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#4B5563] dark:text-[#A0A0A0] rounded-xl text-sm font-semibold hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onNavigate}
              className="px-4 py-2.5 bg-[#62397f] hover:bg-[#7C3AED] text-white rounded-xl text-sm font-semibold shadow-lg shadow-purple-200 dark:shadow-none transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              <Settings size={18} />
              Go to Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
